import React from 'react';
import { Trans } from 'react-i18next';
import ThemeConstants from '../../config/theme/theme_constants';
import { useTranslation } from '../../i18n/useTranslation';
import DocumentBackButton from './document_back_button';

const theme = ThemeConstants();

const UvibesHelp: React.FC = () => {
  return (
    <div className='flex flex-col min-h-dvh w-full relative' style={{ background: `linear-gradient(90deg, ${theme.UvibesPinkGradient} 0%, ${theme.UvibesOrangeGradient} 75%)` }}>
      <DocumentBackButton />
      <Header />
      <div className="rounded-t-[30px] bg-white px-4 py-8 w-full space-y-12">
        <Introduction />
        <PoserQuestions />
        <CommunauteBienveillante />
        <CarteVisite />
        <Accompagnement />
        <Footer />
      </div>
    </div>
  );
};

const Header: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="text-white p-12 text-center">
      <div className="space-y-6">
        <div className="text-6xl">💬</div>
        <h1 className="text-4xl font-bold leading-tight">
          {t('help.title')}
        </h1>
        <div className="w-24 h-1 bg-white/60 mx-auto rounded-full"></div>
        <p className="text-xl text-white/90 max-w-2xl mx-auto">
          {t('help.welcome')}
        </p>
      </div>
    </div>
  );
};

const Introduction: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="text-center space-y-6">
      <div className="border-l-4 p-6 rounded-r-lg" style={{ borderColor: theme.UvibesPink, backgroundColor: theme.UvibesPink + '20' }}>
        <p className="text-lg text-gray-700 leading-relaxed">
          <Trans
            i18nKey="help.intro.p1"
            components={{ brand: <span className="font-semibold" style={{ color: theme.UvibesPink }} /> }}
          />
        </p>
        <p className="text-gray-600 mt-4">
          {t('help.intro.p2')}
        </p>
      </div>
    </section>
  );
};

const PoserQuestions: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-8">
      <SectionTitle
        number="1"
        title={t('help.questions.title')}
        emoji="💬"
      />

      <div className="space-y-6 text-gray-700 leading-relaxed">
        <p>
          {t('help.questions.p1')}
        </p>
        <p>
          {t('help.questions.p2')}
        </p>

        <div className="p-6 rounded-lg border-l-4" style={{ borderColor: theme.UvibesPink, backgroundColor: theme.UvibesPink + '20' }}>
          <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            {t('help.questions.tips_title')}
          </h4>

          <div className="space-y-6">
            <QuestionCategory
              title={t('help.questions.open.title')}
              description={t('help.questions.open.description')}
              examples={[
                t('help.questions.open.example_1'),
                t('help.questions.open.example_2')
              ]}
              emoji="🔓"
              color={theme.UvibesBlue}
            />

            <QuestionCategory
              title={t('help.questions.positive.title')}
              description={t('help.questions.positive.description')}
              examples={[
                t('help.questions.positive.example_1'),
                t('help.questions.positive.example_2')
              ]}
              emoji="✨"
              color={theme.UvibesGreen}
            />

            <QuestionCategory
              title={t('help.questions.personal.title')}
              description={t('help.questions.personal.description')}
              examples={[
                t('help.questions.personal.example_1'),
                t('help.questions.personal.example_2')
              ]}
              emoji="🤝"
              color={theme.UvibesYellow}
            />

            <QuestionCategory
              title={t('help.questions.clear.title')}
              description={t('help.questions.clear.description')}
              examples={[
                t('help.questions.clear.example_1'),
                t('help.questions.clear.example_2')
              ]}
              emoji="💡"
              color={theme.UvibesOrange}
            />

            <QuestionCategory
              title={t('help.questions.neutral.title')}
              description={t('help.questions.neutral.description')}
              examples={[
                t('help.questions.neutral.example_1'),
                t('help.questions.neutral.example_2')
              ]}
              emoji="⚖️"
              color={theme.UvibesRed}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const CommunauteBienveillante: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-8">
      <SectionTitle
        number="2"
        title={t('help.community.title')}
        emoji="🛡️"
      />

      <div className="space-y-6 text-gray-700 leading-relaxed">
        <p>
          {t('help.community.intro')}
        </p>

        <div className="border-l-4 p-6 rounded-r-lg" style={{ borderColor: theme.UvibesYellow, backgroundColor: theme.UvibesYellow + '20' }}>
          <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            {t('help.community.tools_title')}
          </h4>

          <div className="space-y-4">
            <ToolCard
              title={t('help.community.derail.title')}
              description={t('help.community.derail.description')}
              emoji="⚠️"
              color={theme.UvibesOrange}
            />

            <ToolCard
              title={t('help.community.stop.title')}
              description={
                <div className="space-y-2">
                  <p>{t('help.community.stop.p1')}</p>
                  <p>{t('help.community.stop.p2')}</p>
                  <ul className="ml-4 space-y-1">
                    <li>• {t('help.community.stop.option_leave')}</li>
                    <li>• {t('help.community.stop.option_report')}</li>
                  </ul>
                </div>
              }
              emoji="🛑"
              color={theme.UvibesRed}
            />
          </div>

          <div className="mt-4 p-4 bg-blue-100 rounded-lg">
            <p className="text-sm text-blue-800">
              {t('help.community.stop_notice')}
            </p>
          </div>
        </div>

        <SignalementSection />
      </div>
    </section>
  );
};

const SignalementSection: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div className="border-l-4 p-6 rounded-r-lg" style={{ borderColor: theme.UvibesRed, backgroundColor: theme.UvibesRed + '20' }}>
        <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          {t('help.report.title')}
        </h4>
        <p className="mb-4">
          {t('help.report.why')}
        </p>
        <p className="mb-4">{t('help.report.when')}</p>

        <div className="grid md:grid-cols-2 gap-3">
          {[
            { text: t('help.report.cases.discrimination'), emoji: "🚫" },
            { text: t('help.report.cases.harassment'), emoji: "😰" },
            { text: t('help.report.cases.scam'), emoji: "💰" },
            { text: t('help.report.cases.violent'), emoji: "🔞" },
            { text: t('help.report.cases.other'), emoji: "⚖️" }
          ].map((item, index) => (
            <div key={index} className="flex items-center gap-2">
              <span className="text-red-500 text-lg mt-0.5">{item.emoji}</span>
              <span className="text-sm">{item.text}</span>
            </div>
          ))}
        </div>

        <p className="mt-4">{t('help.report.distress')}</p>

        <div className="mt-4 p-3 bg-yellow-100 rounded-lg">
          <p className="text-sm text-yellow-800">
            {t('help.report.tip')}
          </p>
        </div>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg">
        <h4 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          {t('help.report.handling_title')}
        </h4>

        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <span className="text-2xl">👀</span>
            <div>
              <h5 className="font-medium text-gray-800">{t('help.report.review.title')}</h5>
              <p className="text-sm text-gray-600">
                {t('help.report.review.description')}
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className="text-2xl">✅</span>
            <div>
              <h5 className="font-medium text-gray-800">{t('help.report.actions.title')}</h5>
              <p className="text-sm text-gray-600 mb-2">{t('help.report.actions.intro')}</p>
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• <Trans i18nKey="help.report.actions.warning" components={{ strong: <strong /> }} /></li>
                <li>• <Trans i18nKey="help.report.actions.suspension" components={{ strong: <strong /> }} /></li>
                <li>• <Trans i18nKey="help.report.actions.authorities" components={{ strong: <strong /> }} /></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-4 p-4 border-l-4 rounded-r-lg" style={{ borderColor: theme.UvibesOrange, backgroundColor: theme.UvibesOrange + '20' }}>
          <p className="text-sm text-orange-800">
            {t('help.report.legal.intro')}
          </p>
          <ul className="text-sm text-orange-800 mt-2 ml-4 space-y-1">
            <li>• <Trans i18nKey="help.report.legal.civil" components={{ strong: <strong /> }} /></li>
            <li>• <Trans i18nKey="help.report.legal.criminal" components={{ strong: <strong /> }} /></li>
          </ul>
        </div>
      </div>
    </div>
  );
};

const CarteVisite: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-8">
      <SectionTitle
        number="3"
        title={t('help.card.title')}
        emoji="👤"
      />

      <div className="space-y-6 text-gray-700 leading-relaxed">
        <p>
          {t('help.card.p1')}
        </p>
        <p>
          {t('help.card.p2')}
        </p>

        <div className="border-l-4 p-6 rounded-r-lg" style={{ borderColor: theme.UvibesGreen, backgroundColor: theme.UvibesGreen + '20' }}>
          <p className="mb-4">
            {t('help.card.p3')}
          </p>

          <div className="bg-blue-100 p-4 rounded-lg">
            <p className="text-sm text-blue-800 mb-3">
              {t('help.card.usage')}
            </p>

            <p className="text-sm text-blue-800">
              {t('help.card.customize')}
            </p>
            <ul className="text-sm text-blue-800 mt-2 ml-4 space-y-1">
              <li>• {t('help.card.item_contact')}</li>
              <li>• {t('help.card.item_interests')}</li>
              <li>• {t('help.card.item_availability')}</li>
              <li>• {t('help.card.item_goals')}</li>
            </ul>
          </div>

          <div className="mt-4 p-3 bg-yellow-100 rounded-lg">
            <p className="text-sm text-yellow-800">
              {t('help.card.end')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const Accompagnement: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="space-y-8">
      <SectionTitle
        number="4"
        title={t('help.support.title')}
        emoji="📧"
      />

      <div className="space-y-6 text-gray-700 leading-relaxed">
        <p>
          {t('help.support.p1')}
        </p>

        <div className="border-l-4 p-6 rounded-r-lg" style={{ borderColor: theme.UvibesPink, backgroundColor: theme.UvibesPink + '20' }}>
          <p className="mb-4">
            {t('help.support.p2')}
          </p>

          <div className="flex items-center gap-3 bg-white p-4 rounded-lg border-2" style={{ borderColor: theme.UvibesPink + '20' }}>
            <span className="text-2xl">📧</span>
            <a href="mailto:contact@uvibes.fr" className="text-lg font-semibold" style={{ color: theme.UvibesPink }}>
              contact@uvibes.fr
            </a>
          </div>

          <div className="mt-4 p-3 bg-blue-100 rounded-lg">
            <p className="text-sm text-blue-800">
              {t('help.support.hint')}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="text-center space-y-6 py-8 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
      <div className="space-y-4">
        <p className="text-lg text-gray-700">
          <Trans
            i18nKey="help.footer.p1"
            components={{ highlight: <span className="font-semibold" style={{ color: theme.UvibesPink }} /> }}
          />
        </p>
        <p className="text-xl font-medium" style={{ color: theme.UvibesPink }}>
          {t('help.footer.p2')}
        </p>
        <div className="text-2xl font-bold" style={{ color: theme.UvibesPink }}>
          {t('help.footer.p3')}
        </div>
      </div>
      <div className="text-4xl">🎉</div>
    </div>
  );
};

interface SectionTitleProps {
  number: string;
  title: string;
  emoji: string;
}

const SectionTitle: React.FC<SectionTitleProps> = ({ number, title, emoji }) => {
  return (
    <div className="border-l-4 pl-4" style={{ borderColor: theme.UvibesPink }}>
      <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
        <span style={{ color: theme.UvibesPink }}>{number}</span>
        <span className="text-2xl">{emoji}</span>
        <span>{title}</span>
      </h2>
    </div>
  );
};

interface QuestionCategoryProps {
  title: string;
  description: string;
  examples: string[];
  emoji: string;
  color: string;
}

const QuestionCategory: React.FC<QuestionCategoryProps> = ({ title, description, examples, emoji, color }) => {
  const { t } = useTranslation();

  return (
    <div className='p-4 rounded-lg border-2' style={{ borderColor: color, backgroundColor: color + '50' }}>
      <h5 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
        <span className="text-lg">{emoji}</span>
        <span>• {title}</span>
      </h5>
      <p className="text-sm text-gray-600 mb-3">{description}</p>
      <div className="space-y-1">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('help.questions.examples_label')}</p>
        {examples.map((example, index) => (
          <p key={index} className="text-sm text-gray-700 ml-4">◦ {example}</p>
        ))}
      </div>
    </div>
  );
};

interface ToolCardProps {
  title: string;
  description: React.ReactNode;
  emoji: string;
  color: string;
}

const ToolCard: React.FC<ToolCardProps> = ({ title, description, emoji, color }) => {
  return (
    <div className='p-4 rounded-lg border-2' style={{ borderColor: color, backgroundColor: color + '50' }}>
      <h5 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
        <span className="text-lg">{emoji}</span>
        <span>• {title}</span>
      </h5>
      <div className="text-sm text-gray-700">
        {description}
      </div>
    </div>
  );
};

export default UvibesHelp;
